import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import propTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import EditAndDuplicateCampaignsModal from './EditAndDuplicateCampaignsModal';
import usePost from '../../../../Hooks/usePost';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import useRequest from '../../../../Hooks/useRequest';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../../../Contexts/AuthContext';
import axios from 'axios';
import { StyledButton } from '../../../../Components/Generics.styled';
import Checkbox from '@mui/material/Checkbox';
import BarChartIcon from '@mui/icons-material/BarChart';

const ViewAdvertisersContainer = styled.div`
    min-height: 900px;
    display: flex;
    margin-left: auto;
    margin-right: auto;
    flex-direction: column;
`;

const Title = styled.div`
    margin-top: 50px;
    font-size: 23px;
    font-weight: bold;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    height: 50px;
    flex-direction: column;
    font-weight: bold;
    text-align: center;
`;

const StyledNavButtons = styled.div`
    display: flex;
    justify-content: space-evenly;
    width: 500px;
    height: 70px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 30px;
`;

const StyledDuplicateButton = styled.div`
    cursor: pointer;
`;

const StyledTableContainer = styled.div`
    margin-top: 40px;
`;

const StyledCheckboxCell = styled(TableCell)`
    padding: 4px;
`;

const ConnectedViewCampaigns = (props) => {
    const { currentAdvertiser } = props;
    const { currentUser } = useAuth();
    const { accessToken } = { ...currentUser };
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const [selectedRows, setSelectedRows] = useState([]);

    const [tableRows, setTableRows] = useState([]);
    const [postData, setPostData] = useState({
        success: false,
        error: false,
        isLoading: false,
        message: [''],
    });

    const [compareTypeFlag, setCompareTypeFlag] = useState('Multi');

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [campaignData, setCampaignData] = useState({
        headline: '',
        headline2: '',
        headline3: '',
        headline4: '',
        headline5: '',
        headline6: '',
        description: '',
        description2: '',
        description3: '',
        description4: '',
        websiteURL: '',
        googleCampaignKeywords: [],
        budget: 0,
        startDate: '',
        endDate: '',
    });
    const [campaignLocations, setCampaignLocations] = useState([]);

    const selectedAdvertiserId = currentAdvertiser?.advertiserId;

    const { data, mutate } = useRequest(`/GetGoogleCampaignsByAdvertiser/${selectedAdvertiserId}`);
    const { data: pontiacCampaignData, mutate: pontiacMutate } = useRequest(`/GetPontiacCampaignsByAdvertiserId/${selectedAdvertiserId}`);

    const { post: postCreatePaymentIntent } = usePost('/stripe/createPaymentIntent');
    const { post: postNewGoogleCampaign } = usePost('/postNewGoogleCampaign');
    const { post: postNewPontiacCampaign } = usePost('/postNewPontiacCampaign');

    const stripeCustomerId = currentAdvertiser?.stripeCustomerId;
    const { data: paymentMethodData } = useRequest(`/stripe/getCustomer/${stripeCustomerId}`);
    const defaultPaymentMethodId = paymentMethodData?.invoice_settings?.default_payment_method;
    const shouldChargeCreditCard = currentAdvertiser?.chargeCreditCard;

    const history = useHistory();

    const mergedArray = data?.concat(pontiacCampaignData);
    const noCampaigns = mergedArray?.length === 0;

    const createData = (data) => {
        return {
            agencyCompanyName: data?.agencyCompanyName || data?.advertiserCompanyName,
            advertiserCompanyName: data?.advertiserCompanyName,
            campaignName: data?.campaignName,
            campaignId: data?.googleCampaignId || data?.pontiacCampaignId,
            status: data?.status,
            campaignTypeCategory: data?.campaignTypeCategory,
            campaignType: data?.campaignType,
            submittedOn: data?.submittedOn,
            startDate: data?.startDate,
            endDate: data?.endDate,
            budget: data?.budget,
            campaignTypeId: data?.campaignTypeId,
        };
    };

    const addDataToRows = (mergedData) => {
        let sortedData = [];
        if (mergedData && mergedData.length > 0) {
            sortedData = [...mergedData].sort((a, b) => {
                const startDateA = new Date(a.startDate);
                const startDateB = new Date(b.startDate);
                return startDateA - startDateB;
            });
        }

        const rows = sortedData.map((item) => createData(item));

        setTableRows(rows);
    };

    const [date, setDate] = useState([null, null]);
    const handleSetCampaignId = async (campaignId, campaignType) => {
        const options = {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=UTF-8',
                Authorization: `Bearer ${accessToken}`,
            },
        };

        if (campaignId && campaignType === 'Google') {
            const selectedCampaignData = await axios.get(`${baseURL}/GetGoogleCampaignsByCampaign/${campaignId}`, options);
            setDate([null, null]);
            setCampaignData({ ...selectedCampaignData?.data, startDate: '', endDate: '' });
            setModalIsOpen(true);
        }
        if (campaignId && campaignType === 'Programmatic Display') {
            const selectedCampaignData = await axios.get(`${baseURL}/GetPontiacCampaignByPontiacCampaignId/${campaignId}`, options);

            setDate([null, null]);
            setCampaignData({
                ...selectedCampaignData?.data,
                campaignType: selectedCampaignData?.data.campaignTypeName,
                categoryName: selectedCampaignData?.data.categoryName,
                websiteURL: selectedCampaignData?.data?.pontiacCreative.clickURL,
                startDate: '',
                endDate: '',
            });
            setModalIsOpen(true);
        }
    };

    const handleViewCampaignAnalytics = (campaignId, campaignType) => {
        history.push(`/portal/analytics/viewCampaignAnalytics?campaignId=${campaignId}&campaignType=${campaignType}`);
    };

    useEffect(() => {
        addDataToRows(mergedArray);
    }, [data, pontiacCampaignData, selectedAdvertiserId]);

    const formatLocationData = () => {
        const array = [];
        if (isGoogle) {
            campaignData?.googleCampaignLocations.map((item) => {
                array.push({
                    canonicalName: item.canonicalName,
                    reach: item.reach,
                    constantId: item.constantId,
                });
            });
            setCampaignLocations(array);
        }

        if (isProgrammatic) {
            campaignData?.pontiacCampaignLocations.map((item) => {
                array.push({
                    name: `${item.name ? item.name : ''}, ${item.countyName ? item.countyName : ''}, ${item.stateName ? item.stateName : ''}`,
                    zipCodes: item.zipCodes,
                });
            });
            setCampaignLocations(array);
        }
    };

    useEffect(() => {
        if (campaignData?.pontiacCampaignLocations || campaignData?.googleCampaignLocations) {
            formatLocationData();
        }
    }, [campaignData?.pontiacCampaignLocations, campaignData?.googleCampaignLocations]);

    const saveGoogleCampaign = async () => {
        setPostData({
            ...postData,
            isLoading: true,
        });
        try {
            //Charge Customer
            if (shouldChargeCreditCard) {
                //if not on free plan, then charge
                const methodsResp = await postCreatePaymentIntent(
                    {
                        customerId: stripeCustomerId,
                        amount: campaignData.budget * 100,
                        currency: 'usd',
                        payment_method_types: ['card'],
                        paymentMethodId: defaultPaymentMethodId,
                    },
                    'POST'
                );

                const paymentIntentId = methodsResp.data.id;
                if (paymentIntentId) {
                    const googlePostBody = {
                        campaignTypeId: campaignData.campaignTypeId,
                        campaignTypeCategoryId: campaignData.campaignTypeCategoryId,
                        advertiserId: campaignData.advertiserId,
                        agencyId: campaignData.agencyId,
                        startDate: campaignData.startDate,
                        endDate: campaignData.endDate,
                        campaignName: campaignData.campaignName,
                        headline: campaignData.headline,
                        headline2: campaignData.headline2,
                        headline3: campaignData.headline3,
                        headline4: campaignData.headline4,
                        headline5: campaignData.headline5,
                        headline6: campaignData.headline6,
                        description: campaignData.description,
                        description2: campaignData.description2,
                        description3: campaignData.description3,
                        description4: campaignData.description4,
                        websiteURL: campaignData.websiteURL,
                        budget: campaignData.budget,
                        stripePaymentId: paymentIntentId,
                        googleCampaignKeywords: campaignData.googleCampaignKeywords,
                        googleCampaignLocations: campaignLocations,
                    };
                    //post to google
                    const googleAdsResp = await postNewGoogleCampaign(googlePostBody, 'POST');

                    const googleAdsPostSuccessful = googleAdsResp.data === true;

                    if (googleAdsPostSuccessful) {
                        await mutate();
                        setPostData({
                            isLoading: false,
                            success: true,
                            error: false,
                            message: [''],
                        });
                        setModalIsOpen(false);
                    } else {
                        setPostData({
                            isLoading: false,
                            success: false,
                            error: true,
                            message: ['There was a problem posting your ad to google. '],
                        });
                    }
                } else {
                    setPostData({
                        isLoading: false,
                        success: false,
                        error: true,
                        message: ['There was a problem proccessing your payment.'],
                    });
                }
            } else {
                // user is on Free Plan just post to google
                const googlePostBody = {
                    campaignTypeId: campaignData.campaignTypeId,
                    campaignTypeCategoryId: campaignData.campaignTypeCategoryId,
                    advertiserId: campaignData.advertiserId,
                    agencyId: campaignData.agencyId,
                    startDate: campaignData.startDate,
                    endDate: campaignData.endDate,
                    campaignName: campaignData.campaignName,
                    headline: campaignData.headline,
                    headline2: campaignData.headline2,
                    headline3: campaignData.headline3,
                    headline4: campaignData.headline4,
                    headline5: campaignData.headline5,
                    headline6: campaignData.headline6,
                    description: campaignData.description,
                    description2: campaignData.description2,
                    description3: campaignData.description3,
                    description4: campaignData.description4,
                    websiteURL: campaignData.websiteURL,
                    budget: campaignData.budget,
                    stripePaymentId: 'Not Applicable',
                    googleCampaignKeywords: campaignData.googleCampaignKeywords,
                    googleCampaignLocations: campaignLocations,
                };

                const googleAdsResp = await postNewGoogleCampaign(googlePostBody, 'POST');
                const googleAdsPostSuccessful = googleAdsResp.data === true;

                if (googleAdsPostSuccessful) {
                    await mutate();
                    setPostData({
                        isLoading: false,
                        success: true,
                        error: false,
                        message: [''],
                    });
                    setModalIsOpen(false);
                }
            }
        } catch (err) {
            setPostData({
                isLoading: false,
                success: false,
                error: true,
                message: ['Something went wrong, Please try again later.'],
            });
        }
    };

    const savePontiacCampaign = async () => {
        setPostData({
            ...postData,
            isLoading: true,
        });

        try {
            //Charge Customer
            if (shouldChargeCreditCard) {
                //if not on free plan, then charge
                const methodsResp = await postCreatePaymentIntent(
                    {
                        customerId: stripeCustomerId,
                        amount: campaignData.budget * 100,
                        currency: 'usd',
                        payment_method_types: ['card'],
                        paymentMethodId: defaultPaymentMethodId,
                    },
                    'POST'
                );

                const paymentIntentId = methodsResp.data.id;
                if (paymentIntentId) {
                    const pontiacCreativeId = campaignData?.pontiacCreativeId;
                    if (pontiacCreativeId) {
                        const pontiacPostBody = {
                            campaignTypeId: campaignData.campaignTypeId,
                            campaignTypeCategoryId: campaignData.campaignTypeCategoryId,
                            advertiserId: campaignData.advertiserId,
                            agencyId: campaignData.agencyId,
                            startDate: campaignData.startDate,
                            endDate: campaignData.endDate,
                            campaignName: campaignData.campaignName,
                            budget: campaignData.budget,
                            stripePaymentId: paymentIntentId,
                            pontiacCreativeId: pontiacCreativeId,
                            pontiacCampaignLocations: campaignLocations,
                        };
                        //post to pontiac
                        const pontiacAdsResp = await postNewPontiacCampaign(pontiacPostBody, 'POST');
                        if (pontiacAdsResp.data === true) {
                            await pontiacMutate();
                            setPostData({
                                isLoading: false,
                                success: true,
                                error: false,
                                message: [''],
                            });
                            setModalIsOpen(false);
                        } else {
                            setPostData({
                                isLoading: false,
                                success: false,
                                error: true,
                                message: ['There was an error creating your pontiac campaign.'],
                            });
                        }
                    } else {
                        setPostData({
                            isLoading: false,
                            success: false,
                            error: true,
                            message: ['File upload failed, please try again.'],
                        });
                    }
                } else {
                    setPostData({
                        isLoading: false,
                        success: false,
                        error: true,
                        message: ['Payment failed, please try again.'],
                    });
                }
            } else {
                const pontiacCreativeId = campaignData?.pontiacCreativeId;
                if (pontiacCreativeId) {
                    const pontiacPostBody = {
                        campaignTypeId: campaignData.campaignTypeId,
                        campaignTypeCategoryId: campaignData.campaignTypeCategoryId,
                        advertiserId: campaignData.advertiserId,
                        agencyId: campaignData.agencyId,
                        startDate: campaignData.startDate,
                        endDate: campaignData.endDate,
                        campaignName: campaignData.campaignName,
                        budget: campaignData.budget,
                        stripePaymentId: 'Not Applicable',
                        pontiacCreativeId: pontiacCreativeId,
                        pontiacCampaignLocations: campaignLocations,
                    };

                    //post to pontiac
                    const pontiacAdsResp = await postNewPontiacCampaign(pontiacPostBody, 'POST');

                    if (pontiacAdsResp.data === true) {
                        await pontiacMutate();
                        setPostData({
                            isLoading: false,
                            success: true,
                            error: false,
                            message: [''],
                        });
                        setModalIsOpen(false);
                    } else {
                        setPostData({
                            isLoading: false,
                            success: false,
                            error: true,
                            message: ['There was an issue created your pontiac campaign.'],
                        });
                    }
                } else {
                    setPostData({
                        isLoading: false,
                        success: false,
                        error: true,
                        message: ['There was a problem uploading your file'],
                    });
                }
            }
        } catch (err) {
            setPostData({
                isLoading: false,
                success: false,
                error: true,
                message: ['Something went wrong, Please try again later.'],
            });
        }
    };

    const isGoogle = campaignData?.campaignTypeId === 1;
    const isProgrammatic = campaignData?.campaignTypeId === 26;
    const isMobile = campaignData?.campaignTypeId === 2;

    const handlePostDuplicate = async () => {
        if (isGoogle) {
            await saveGoogleCampaign();
        } else if (isProgrammatic) {
            await savePontiacCampaign();
        }
    };

    const determineTypeFlag = () => {
        const allPontiac = selectedRows.every((item) => item.campaignType === 'Programmatic');
        const allGoogle = selectedRows.every((item) => item.campaignType === 'Google');
        const hasPontiac = selectedRows.some((item) => item.campaignType === 'Programmatic');
        const hasGoogle = selectedRows.some((item) => item.campaignType === 'Google');

        let flag = '';

        if (allPontiac && hasPontiac && !hasGoogle) {
            flag = 'Programmatic';
        } else if (hasPontiac && hasGoogle) {
            flag = 'Multi';
        } else if (allGoogle && hasGoogle && !hasPontiac) {
            flag = 'Google';
        }
        setCompareTypeFlag(flag);
    };

    useEffect(() => {
        determineTypeFlag();
    }, [selectedRows]);

    const handleRowCheckboxChange = (row) => {
        const newSelectedRows = [...selectedRows];
        const rowIndex = newSelectedRows.findIndex((selectedRow) => selectedRow === row);

        if (rowIndex !== -1) {
            newSelectedRows.splice(rowIndex, 1);
        } else {
            newSelectedRows.push(row);
        }

        setSelectedRows(newSelectedRows);
    };

    const isSelected = (row) => selectedRows.includes(row);

    const compareMet = selectedRows.length > 1 && selectedRows.length <= 5;

    return (
        <>
            <ViewAdvertisersContainer>
                <Title>Campaign List</Title>
                <StyledTableContainer>
                    <div>
                        <div
                            style={{
                                width: '100%',
                                maxWidth: '1500px',
                                display: 'table',
                                tableLayout: 'fixed',
                            }}
                        >
                            <TableContainer
                                style={{
                                    border: '1px solid black',
                                    borderRadius: '12px',
                                    backgroundColor: ' #fafbf7',
                                }}
                                component={Paper}
                            >
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Compare</TableCell>
                                            <TableCell align="right">Campaign Name</TableCell>
                                            <TableCell align="right">Agency Name</TableCell>
                                            <TableCell align="right">Advertiser Name</TableCell>
                                            <TableCell align="right">Campaign Id</TableCell>
                                            <TableCell align="right">Campaign Type</TableCell>
                                            <TableCell align="right">Campaign Category</TableCell>
                                            <TableCell align="right">Submitted On</TableCell>
                                            <TableCell align="right">Start Date</TableCell>
                                            <TableCell align="right">End Date</TableCell>
                                            <TableCell align="right">Budget</TableCell>
                                            <TableCell align="right">Status</TableCell>
                                            <TableCell align="center">Duplicate</TableCell>
                                            <TableCell align="center">Analytics</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableRows.map((row, idx) => (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <StyledCheckboxCell padding="checkbox">
                                                    <Checkbox
                                                        // Use the `isSelected` function to determine checkbox state
                                                        checked={isSelected(row)}
                                                        // Use the `handleRowCheckboxChange` function to handle checkbox state changes
                                                        onChange={() => handleRowCheckboxChange(row)}
                                                    />
                                                </StyledCheckboxCell>
                                                <TableCell align="right">{row.campaignName}</TableCell>
                                                <TableCell align="right">{row.agencyCompanyName}</TableCell>
                                                <TableCell align="right">{row.advertiserCompanyName}</TableCell>
                                                <TableCell align="right">{row.campaignId}</TableCell>
                                                <TableCell align="right">{row.campaignType}</TableCell>
                                                <TableCell align="right">{row.campaignTypeCategory}</TableCell>
                                                <TableCell align="right">{dayjs(row.submittedOn).format('MM/DD/YYYY')}</TableCell>
                                                <TableCell align="right">{dayjs(row.startDate).format('MM/DD/YYYY')}</TableCell>
                                                <TableCell align="right">{dayjs(row.endDate).format('MM/DD/YYYY')}</TableCell>
                                                <TableCell align="right">{row.budget}</TableCell>
                                                <TableCell align="right">{row.status}</TableCell>

                                                <TableCell align="center">
                                                    <StyledDuplicateButton onClick={() => handleSetCampaignId(row.campaignId, row.campaignType)}>
                                                        <ContentCopyIcon color="primary" />
                                                    </StyledDuplicateButton>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <StyledDuplicateButton
                                                        onClick={() => handleViewCampaignAnalytics(row.campaignId, row.campaignType)}
                                                    >
                                                        <BarChartIcon color="primary" />
                                                    </StyledDuplicateButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </StyledTableContainer>
                {noCampaigns ? (
                    <div
                        style={{
                            textAlign: 'center',
                            backgroundColor: 'white',
                        }}
                    >
                        There are no saved campaigns for this advertiser
                    </div>
                ) : null}
                {compareMet ? (
                    <StyledButton
                        style={{ marginLeft: 'auto', marginTop: '10px' }}
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            history.push(
                                `/portal/analytics/viewCompareAnalytics?${selectedRows
                                    .map((item, idx) => `campaign${idx + 1}Id=${item.campaignId}&campaign${idx + 1}TypeId=${item.campaignTypeId}`)
                                    .join('&')}&campaignType=${compareTypeFlag}`
                            )
                        }
                    >
                        Compare
                    </StyledButton>
                ) : null}

                <StyledNavButtons>
                    <StyledButton variant="contained" color="primary" onClick={() => history.push('/portal/campaign/createACampaign')}>
                        Create A New Campaign
                    </StyledButton>
                </StyledNavButtons>
            </ViewAdvertisersContainer>
            <EditAndDuplicateCampaignsModal
                modalIsOpen={modalIsOpen}
                setModalIsOpen={setModalIsOpen}
                campaignData={campaignData}
                setCampaignData={setCampaignData}
                date={date}
                setDate={setDate}
                postData={postData}
                handlePostDuplicate={handlePostDuplicate}
            />
        </>
    );
};

ConnectedViewCampaigns.propTypes = {
    advertisers: propTypes.shape({
        advertiserList: propTypes.array,
    }),
    profile: propTypes.shape({
        isAgency: propTypes.bool,
        isIndividual: propTypes.bool,
        isSiteAdmin: propTypes.bool,
    }),
    campaigns: propTypes.shape({
        linkedAdvertiserCampaigns: propTypes.array,
        selectedCampaign: propTypes.shape({
            dealName: propTypes.string,
        }),
    }),
    currentAdvertiser: propTypes.shape({
        advertiserId: propTypes.number,
        stripeCustomerId: propTypes.string,
        chargeCreditCard: propTypes.bool,
    }),
};

ConnectedViewCampaigns.defaultProps = {
    advertisers: {},
    setAdvertisers: () => {},
    campaigns: { selectedCampaign: { dealName: '' } },
};
export default ConnectedViewCampaigns;
